/**
 * Shared rules for NEXT Semantica timeline: same block list and scores as
 * CoherenceSemanticsPanel (segment strategy + score mode).
 */

const IDEA_PAUSE_SPLIT_MS = 900;
const IDEA_MAX_WORDS_PER_PHRASE = 30;
const IDEA_TERMINAL_PUNCTUATION_RE = /[.!?…]+(?:["')\]]+)?$/;
const IDEA_COMMA_PUNCTUATION_RE = /,(?:["')\]]+)?$/;
const IDEA_SOFT_MAX_WORDS_PER_PHRASE = 14;
const IDEA_MIN_WORDS_TO_SPLIT = 3;

const IDEA_BREAK_BEFORE_WORDS = new Set([
  'pues',
  'porque',
  'que',
  'cuando',
  'mientras',
  'aunque',
  'pero',
  'y',
  'ademas',
  'además',
  'entonces',
  'luego',
  'despues',
  'después',
  'en',
  'lo',
  'me',
  'tuve',
  'pedirles',
  'habia',
  'había',
]);

const IDEA_SINGLE_TOKEN_STARTERS = new Set(['vale', 'bueno', 'nada']);
const IDEA_TINY_FRAGMENT_WORDS = new Set([
  'de',
  'del',
  'la',
  'el',
  'los',
  'las',
  'lo',
  'en',
  'y',
  'o',
  'que',
  'a',
  'al',
  'con',
  'por',
  'para',
  'me',
  'se',
]);

function normalizeFiniteNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function normalizeWords(words) {
  if (!Array.isArray(words)) return [];
  return words
    .map((word) => {
      const wordIndex = normalizeFiniteNumber(word?.word_index ?? word?.wordIndex);
      const startTimeMs = normalizeFiniteNumber(word?.start_time_ms ?? word?.startTimeMs);
      const endTimeMs = normalizeFiniteNumber(word?.end_time_ms ?? word?.endTimeMs);
      if (wordIndex == null || startTimeMs == null || endTimeMs == null) return null;
      return {
        ...word,
        word_index: wordIndex,
        start_time_ms: startTimeMs,
        end_time_ms: endTimeMs,
        speaker: String(word?.speaker ?? '').trim(),
        text: String(word?.text ?? '').trim(),
        source_segment_index: normalizeFiniteNumber(
          word?.source_segment_index ?? word?.sourceSegmentIndex
        ),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.word_index !== b.word_index) return a.word_index - b.word_index;
      return a.start_time_ms - b.start_time_ms;
    });
}

function getPerAlertBlocks(diarization) {
  const list = diarization?.blocks_per_alert;
  return Array.isArray(list) ? list : [];
}

function scoreAlertMajorFromBlocks(blocks) {
  if (!Array.isArray(blocks) || !blocks.length) return 100;
  let worst = Number.POSITIVE_INFINITY;
  blocks.forEach((block) => {
    const score = normalizeFiniteNumber(
      block?.score_alert_major_after ??
        block?.scoreAlertMajorAfter ??
        block?.aggregate_coherence_after ??
        block?.aggregateCoherenceAfter
    );
    if (score != null) worst = Math.min(worst, score);
  });
  return Number.isFinite(worst) ? worst : 100;
}

function scoreComputedSubtractionFromBlocks(blocks) {
  if (!Array.isArray(blocks) || !blocks.length) return 100;
  let totalReduction = 0;
  blocks.forEach((block) => {
    const score = normalizeFiniteNumber(
      block?.score_resta_computada_after ?? block?.scoreRestaComputadaAfter
    );
    if (score != null) totalReduction += Math.max(0, 100 - score);
  });
  return Math.max(0, 100 - totalReduction);
}

function buildIdeaPhraseChunks(words) {
  if (!Array.isArray(words) || !words.length) return [];
  const chunks = [];
  let current = [];

  const normalizedWordText = (word) =>
    String(word?.text ?? '')
      .trim()
      .toLowerCase()
      .replace(/[.,!?…;:()"'\]]+$/g, '');

  const flush = () => {
    if (!current.length) return;
    chunks.push(current);
    current = [];
  };

  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    const next = i < words.length - 1 ? words[i + 1] : null;
    const nextText = normalizedWordText(next);

    current.push(word);

    if (!next) {
      flush();
      continue;
    }

    const currentTextRaw = String(word?.text ?? '').trim();
    const currentTextNorm = normalizedWordText(word);
    const speakerChanged = String(word?.speaker ?? '') !== String(next?.speaker ?? '');
    const pauseMs = Number(next?.start_time_ms) - Number(word?.end_time_ms);
    const hasTerminalPunctuation = IDEA_TERMINAL_PUNCTUATION_RE.test(currentTextRaw);
    const hasCommaPause = IDEA_COMMA_PUNCTUATION_RE.test(currentTextRaw);
    const reachedHardMaxWords = current.length >= IDEA_MAX_WORDS_PER_PHRASE;
    const reachedSoftMaxWords = current.length >= IDEA_SOFT_MAX_WORDS_PER_PHRASE;
    const shouldBreakBeforeNextConnector =
      current.length >= Math.max(IDEA_MIN_WORDS_TO_SPLIT, 4) &&
      IDEA_BREAK_BEFORE_WORDS.has(nextText);
    const shouldBreakAfterComma = hasCommaPause && current.length >= IDEA_MIN_WORDS_TO_SPLIT;
    const shouldBreakAfterShortStarter =
      current.length === 1 &&
      IDEA_SINGLE_TOKEN_STARTERS.has(currentTextNorm) &&
      IDEA_BREAK_BEFORE_WORDS.has(nextText);

    if (
      speakerChanged ||
      hasTerminalPunctuation ||
      pauseMs > IDEA_PAUSE_SPLIT_MS ||
      reachedHardMaxWords ||
      shouldBreakAfterComma ||
      shouldBreakAfterShortStarter ||
      (reachedSoftMaxWords && shouldBreakBeforeNextConnector)
    ) {
      flush();
    }
  }

  // Avoid awkward one-token fragments such as "de", "la", etc.
  for (let i = 0; i < chunks.length; i += 1) {
    const chunk = chunks[i];
    if (!Array.isArray(chunk) || chunk.length !== 1) continue;
    const onlyWord = normalizedWordText(chunk[0]);
    if (!IDEA_TINY_FRAGMENT_WORDS.has(onlyWord)) continue;

    if (i > 0 && Array.isArray(chunks[i - 1])) {
      chunks[i - 1] = chunks[i - 1].concat(chunk);
      chunks.splice(i, 1);
      i -= 1;
      continue;
    }
    if (i < chunks.length - 1 && Array.isArray(chunks[i + 1])) {
      chunks[i + 1] = chunk.concat(chunks[i + 1]);
      chunks.splice(i, 1);
      i -= 1;
    }
  }

  return chunks;
}

function phraseText(words) {
  return words
    .map((word) => String(word?.text ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

function blockAnchorIndex(block) {
  return normalizeFiniteNumber(block?.anchor_word_index ?? block?.anchorWordIndex);
}

function blockContributingIds(block) {
  const ids = block?.contributing_alert_ids ?? block?.contributingAlertIds;
  if (!Array.isArray(ids)) return [];
  return ids.map((id) => String(id)).filter(Boolean);
}

function noneBlocksFromDiarization(diarization) {
  const perAlertBlocks = getPerAlertBlocks(diarization);
  if (!perAlertBlocks.length) return [];
  const words = normalizeWords(diarization?.words);
  const wordByIndex = new Map(words.map((word) => [word.word_index, word]));

  return perAlertBlocks
    .map((block, index) => {
      const anchorIndex = blockAnchorIndex(block);
      const anchorWord = anchorIndex != null ? wordByIndex.get(anchorIndex) : null;
      const rawStart = normalizeFiniteNumber(block?.start_time_ms ?? block?.startTimeMs);
      const rawEnd = normalizeFiniteNumber(block?.end_time_ms ?? block?.endTimeMs);
      const startTimeMs = anchorWord?.start_time_ms ?? rawStart;
      const endFromAnchor = anchorWord?.end_time_ms;
      const endTimeMs =
        endFromAnchor != null
          ? Math.max(startTimeMs ?? endFromAnchor, endFromAnchor)
          : rawEnd;
      if (startTimeMs == null || endTimeMs == null || endTimeMs < startTimeMs) return null;
      const sourceId = block?.block_id ?? block?.blockId ?? `row-${index}`;
      return {
        ...block,
        block_id: `none:${String(sourceId)}:${index}`,
        start_time_ms: startTimeMs,
        end_time_ms: endTimeMs,
        text: anchorWord?.text || String(block?.text ?? '').trim(),
        highlight_word_indexes: anchorIndex != null ? [anchorIndex] : [],
        block_kind: 'none',
      };
    })
    .filter(Boolean);
}

/**
 * Ideas cut in the browser: only for documents written before the engine stored its own ideas
 * (no `blocks_idea` at all). An alert belongs to the one phrase holding its anchor word, never to
 * every phrase its block touches: a gesture counts only in the idea it appears in.
 */
function ideaBlocksFromDiarization(diarization) {
  const words = normalizeWords(diarization?.words);
  if (!words.length) return [];

  const perAlertBlocks = getPerAlertBlocks(diarization);
  const phrases = buildIdeaPhraseChunks(words);
  const result = [];

  phrases.forEach((phraseWords, phraseIndex) => {
    if (!Array.isArray(phraseWords) || !phraseWords.length) return;

    const wordIndexes = phraseWords.map((word) => word.word_index);
    const wordIndexSet = new Set(wordIndexes);
    const startTimeMs = phraseWords[0].start_time_ms;
    const endTimeMs = phraseWords[phraseWords.length - 1].end_time_ms;

    const relatedBlocks = perAlertBlocks.filter((block) => {
      const anchorIndex = blockAnchorIndex(block);
      return anchorIndex != null && wordIndexSet.has(anchorIndex);
    });

    const contributingIds = Array.from(
      new Set(relatedBlocks.flatMap((block) => blockContributingIds(block)))
    );
    const alertMajorScore = scoreAlertMajorFromBlocks(relatedBlocks);
    const computedSubtractionScore = scoreComputedSubtractionFromBlocks(relatedBlocks);
    const anchorBlock = relatedBlocks.reduce((worst, block) => {
      if (!worst) return block;
      const currentWorst = scoreAlertMajorFromBlocks([worst]);
      const nextWorst = scoreAlertMajorFromBlocks([block]);
      if (nextWorst !== currentWorst) return nextWorst < currentWorst ? block : worst;
      const currentAnchor = blockAnchorIndex(worst);
      const nextAnchor = blockAnchorIndex(block);
      if (currentAnchor == null) return block;
      if (nextAnchor == null) return worst;
      return nextAnchor < currentAnchor ? block : worst;
    }, null);

    result.push({
      block_id: `idea:${phraseWords[0].word_index}-${phraseWords[phraseWords.length - 1].word_index}:${phraseIndex}`,
      start_time_ms: startTimeMs,
      end_time_ms: endTimeMs,
      text: phraseText(phraseWords),
      speaker: phraseWords[0].speaker || '',
      source_segment_index:
        phraseWords[0].source_segment_index != null ? phraseWords[0].source_segment_index : -1,
      contributing_alert_ids: contributingIds,
      aggregate_coherence_after: alertMajorScore,
      score_alert_major_after: alertMajorScore,
      score_resta_computada_after: computedSubtractionScore,
      aggregate_alert_count: contributingIds.length,
      aggregate_matched_count: contributingIds.length,
      anchor_word_index: blockAnchorIndex(anchorBlock) ?? phraseWords[0].word_index,
      highlight_word_indexes: wordIndexes,
      block_kind: 'idea',
      parent_block_id: null,
      child_block_ids: relatedBlocks
        .map((block) => block?.block_id ?? block?.blockId)
        .filter((value) => value != null)
        .map((value) => String(value)),
    });
  });

  return result;
}

/**
 * The blocks the coherence views read.
 *
 * Ideas are the only grouping: whatever strategy a stored preference still names ('merged',
 * 'per_alert'), the engine's own ideas are shown — the same cut, scores and alerts the verdict and
 * the metrics read. The phrases cut here only stand in for a document with no `blocks_idea`.
 *
 * @param {object|null} diarization - semanticModes.diarization
 * @param {'idea'|'none'|string} segmentationTab - 'none' is grouping off; anything else is 'idea'
 * @returns {object[]}
 */
export function semanticBlocksForSegmentStrategy(diarization, segmentationTab) {
  if (!diarization) return [];
  if (segmentationTab === 'none') {
    const list = diarization.blocks_none;
    if (Array.isArray(list)) return list;
    return noneBlocksFromDiarization(diarization);
  }
  const list = diarization.blocks_idea;
  if (Array.isArray(list) && list.length) return list;
  return ideaBlocksFromDiarization(diarization);
}

/**
 * The segmentation a stored UI preference maps to: grouping off stays off, everything else is
 * ideas (the merged-overlap and per-alert views are gone).
 *
 * @param {string|null|undefined} segmentationTab
 * @returns {'idea'|'none'}
 */
export function normalizeSegmentationTab(segmentationTab) {
  return segmentationTab === 'none' ? 'none' : 'idea';
}

/**
 * @param {object} block
 * @param {'alert_major'|'computed_subtraction'} scoreMode
 * @returns {number}
 */
export function blockScoreForCoherenceMode(block, scoreMode) {
  if (scoreMode === 'computed_subtraction') {
    const resta = Number(block?.score_resta_computada_after ?? block?.scoreRestaComputadaAfter);
    if (Number.isFinite(resta)) return resta;
  }
  const major = Number(
    block?.score_alert_major_after ??
      block?.scoreAlertMajorAfter ??
      block?.aggregate_coherence_after ??
      block?.aggregateCoherenceAfter
  );
  if (Number.isFinite(major)) return major;
  return 100;
}

/**
 * Recruiter-style band colors (hex) for chart / timeline markers.
 * @param {number} percent
 * @param {number} goodThreshold
 * @param {number} warnThreshold
 * @returns {string}
 */
export function coherenceBandHex(percent, goodThreshold = 80, warnThreshold = 50) {
  const p = Number(percent);
  const good = Number.isFinite(Number(goodThreshold)) ? Number(goodThreshold) : 80;
  const warn = Number.isFinite(Number(warnThreshold)) ? Number(warnThreshold) : 50;
  if (!Number.isFinite(p)) return '#2e7d32';
  if (p >= good) return '#2e7d32';
  if (p >= warn) return '#f57c00';
  return '#c62828';
}
